import { useEffect, useId, useState, type ReactNode } from "react";
import { NOTIFICATION_TYPES, type NotificationType } from "@vmeste/shared";
import {
  useNotifications,
  type LeadField,
  type PushStatus,
  type SaveState,
} from "../app/notifications";
import { Button, Card, CardHeader } from "./ui";
import { ChevronDownIcon } from "./icons";
import { cn } from "../lib/cn";
import { useT, type MessageKey, type Translate } from "../i18n/index.js";

/**
 * Notification settings (SPEC §4.7).
 *
 * The panel's job is to be honest about a genuinely awkward platform situation.
 * Push works everywhere except the one place most of these two people will open
 * the app — Safari on an iPhone that has not been added to the home screen —
 * and the worst thing this screen could do is offer a switch that quietly does
 * nothing. So each dead end says what it is and what, if anything, fixes it.
 *
 * Horizontal safe-area padding comes from AppLayout's `pad-safe-x` wrapper; the
 * card only has to stay inside it.
 */

/**
 * Labels a person recognises. `item.due` means nothing to anybody, and a switch
 * whose name you cannot read is a switch you cannot decide about.
 *
 * The table holds *keys*, not words: it is built once when the module loads, so
 * words put here would be the ones from whatever language was on at that moment
 * and would stay after a switch. `t()` is called where the row is drawn.
 */
const TYPE_LABEL: Record<NotificationType, { title: MessageKey; hint: MessageKey }> = {
  "message.new": {
    title: "settings.typeMessageNew",
    hint: "settings.typeMessageNewHint",
  },
  "item.assigned": {
    title: "settings.typeItemAssigned",
    hint: "settings.typeItemAssignedHint",
  },
  "item.due": {
    title: "settings.typeItemDue",
    hint: "settings.typeItemDueHint",
  },
  "event.soon": {
    title: "settings.typeEventSoon",
    hint: "settings.typeEventSoonHint",
  },
  "wish.added": {
    title: "settings.typeWishAdded",
    hint: "settings.typeWishAddedHint",
  },
  "place.added": {
    title: "settings.typePlaceAdded",
    hint: "settings.typePlaceAddedHint",
  },
  "space.joined": {
    title: "settings.typeSpaceJoined",
    hint: "settings.typeSpaceJoinedHint",
  },
};

const HOUR = 60;
const DAY = 24 * HOUR;

/**
 * The two reminders that arrive *before* something, and how much before.
 *
 * Only these two have a lead time to set — the other four fire because
 * something just happened, and there is no earlier moment to offer. The table
 * is what keeps that a fact about two types rather than a pair of special
 * cases threaded through the list below.
 *
 * A deadline gets a whole extra preset. A task's due date is usually a date
 * rather than a time, so it falls at midnight, and fifteen minutes' notice
 * lands at 23:45 — technically a warning, practically useless. «За день»
 * reaches somebody while there is still a day to do the thing in.
 */
const LEAD: Partial<
  Record<
    NotificationType,
    { field: LeadField; legend: MessageKey; presets: readonly number[] }
  >
> = {
  "event.soon": {
    field: "eventLeadMinutes",
    legend: "settings.leadEventLegend",
    presets: [0, 5, 15, 30, HOUR, 2 * HOUR],
  },
  "item.due": {
    field: "itemLeadMinutes",
    legend: "settings.leadItemLegend",
    presets: [0, 5, 15, 30, HOUR, 2 * HOUR, DAY],
  },
};

/**
 * Minutes, as words.
 *
 * The unit is whichever one divides cleanly, largest first, rather than
 * whichever is closest: 90 stays «за 90 минут» instead of turning into «за 1,5
 * часа». The presets cannot produce a value like that, but the other phone can,
 * and a number this screen cannot read back is the bug it was built to fix.
 */
export function leadWords(t: Translate, minutes: number): string {
  if (minutes <= 0) return t("settings.leadAtMoment");
  if (minutes % DAY === 0) return t("settings.leadDays", { count: minutes / DAY });
  if (minutes % HOUR === 0) return t("settings.leadHours", { count: minutes / HOUR });
  return t("settings.leadMinutes", { count: minutes });
}

export function NotificationSettings() {
  const push = useNotifications();
  const t = useT();
  // Pulled out as a const so the null check below narrows it for the switches.
  const prefs = push.prefs;

  return (
    <Card>
      <CardHeader
        title={t("settings.notifTitle")}
        meta={<SaveBadge state={push.save} />}
      />

      <DeviceState
        status={push.status}
        iosSafari={push.iosSafari}
        busy={push.busy}
        error={push.error}
        onEnable={push.enable}
        onDisable={push.disable}
      />

      {push.prefsLoading ? (
        <p className="border-t border-line-soft px-4 py-3 text-[0.875rem] text-muted">
          {t("settings.notifPrefsLoading")}
        </p>
      ) : push.prefsError ? (
        <div className="flex flex-col items-start gap-2 border-t border-line-soft px-4 py-3">
          <p className="text-[0.875rem] text-neg">{push.prefsError}</p>
          <p className="text-[0.8125rem] text-muted">
            {t("settings.notifPrefsErrorHint")}
          </p>
          <Button
            variant="secondary"
            onClick={push.reloadPrefs}
            className="px-3 text-[0.875rem]"
          >
            {t("common.retry")}
          </Button>
        </div>
      ) : prefs ? (
        <>
          {push.status !== "subscribed" ? (
            <p className="border-t border-line-soft bg-warn-soft px-4 py-3 text-[0.8125rem] text-warn">
              {t("settings.notifDevicesShared")}
            </p>
          ) : null}

          {/*
            `min-w-0` on a fieldset, which looks like noise and is not.

            A fieldset carries `min-inline-size: min-content` from the browser's
            own stylesheet, so it refuses to be narrower than its widest child.
            The scrolling row of presets inside is only willing to scroll once
            something has told it how wide it may be — so the fieldset grew to
            fit all seven pills, took the list with it, and pushed every switch
            past the card's clipped edge. The toggles did not disappear; they
            were shoved off the screen by a rule nobody wrote.
          */}
          <fieldset className="min-w-0 border-t border-line-soft">
            <legend className="sr-only">{t("settings.notifLegend")}</legend>
            <ul className="divide-y divide-line-soft">
              {NOTIFICATION_TYPES.map((type) => {
                const on = prefs.enabled.includes(type);
                const lead = LEAD[type];
                const hint = t(TYPE_LABEL[type].hint);

                return (
                  <li key={type}>
                    <Switch
                      checked={on}
                      onChange={(next) => push.setTypeEnabled(type, next)}
                      title={t(TYPE_LABEL[type].title)}
                      hint={hint}
                      /*
                       * The notice rides in the row, left of the toggle, and
                       * disappears with it.
                       *
                       * It had a row of its own — a scrolling strip of presets
                       * under every switched-on type, which cost a line each
                       * and made the fieldset wider than the phone. There was
                       * always room beside the toggle; the strip was simply
                       * bigger than the question deserved.
                       *
                       * A plain `<select>` rather than a menu of our own: on a
                       * phone it opens the system's wheel, which is a better
                       * way to pick one of seven than a strip somebody has to
                       * scroll, and it is keyboard- and screen-reader-correct
                       * without a line of code from us.
                       */
                      control={
                        lead && on ? (
                          <LeadSelect
                            label={t(lead.legend)}
                            presets={lead.presets}
                            minutes={prefs[lead.field]}
                            onChange={(minutes) =>
                              push.setLeadMinutes(lead.field, minutes)
                            }
                          />
                        ) : null
                      }
                    />
                  </li>
                );
              })}
            </ul>
          </fieldset>

          <QuietHours
            from={prefs.quietFrom}
            to={prefs.quietTo}
            deviceTimeZone={push.deviceTimeZone}
            onChange={push.setQuietHours}
          />
        </>
      ) : null}

      {push.save.kind === "error" ? (
        <div className="flex flex-wrap items-center gap-3 border-t border-line-soft bg-neg-soft px-4 py-3">
          <p className="min-w-0 flex-1 text-[0.875rem] text-neg">
            {t("settings.notifSaveError", { message: push.save.message })}
          </p>
          <Button
            variant="secondary"
            onClick={push.retrySave}
            className="px-3 text-[0.875rem]"
          >
            {t("common.retry")}
          </Button>
        </div>
      ) : null}
    </Card>
  );
}

/* ------------------------------------------------------------ device state */

function DeviceState({
  status,
  iosSafari,
  busy,
  error,
  onEnable,
  onDisable,
}: {
  status: PushStatus;
  iosSafari: boolean;
  busy: boolean;
  error: string | null;
  onEnable(): void;
  onDisable(): void;
}) {
  const t = useT();

  return (
    <div className="flex flex-col gap-2.5 px-4 py-3">
      {status === "checking" ? (
        <p className="text-[0.875rem] text-muted">{t("settings.notifChecking")}</p>
      ) : null}

      {status === "unsupported" ? (
        <Note tone="muted" title={t("settings.notifUnsupportedTitle")}>
          {t("settings.notifUnsupportedBody")}
        </Note>
      ) : null}

      {/*
        The case that matters. On iOS Web Push exists only for an app added to
        the home screen (iOS 16.4+, SPEC §2) — in a Safari tab the system does
        not expose it at all. A subscribe button here would fail every time, so
        there isn't one: only the steps that actually lead somewhere.
      */}
      {status === "needs-install" ? (
        <Note tone="warn" title={t("settings.notifInstallTitle")}>
          <p>{t("settings.notifInstallBody")}</p>
          <ol className="mt-2 flex list-decimal flex-col gap-1 pl-4">
            <li>{t("settings.notifInstallStep1")}</li>
            <li>{t("settings.notifInstallStep2")}</li>
            <li>{t("settings.notifInstallStep3")}</li>
          </ol>
          {!iosSafari ? (
            <p className="mt-2">{t("settings.notifInstallNotSafari")}</p>
          ) : null}
        </Note>
      ) : null}

      {status === "denied" ? (
        <Note tone="neg" title={t("settings.notifDeniedTitle")}>
          <p>{t("settings.notifDeniedBody")}</p>
          <p className="mt-2">
            <b className="font-medium">Safari:</b> {t("settings.notifDeniedSafari")}{" "}
            <b className="font-medium">Chrome:</b> {t("settings.notifDeniedChrome")}
          </p>
        </Note>
      ) : null}

      {status === "idle" ? (
        <>
          <p className="text-[0.875rem] text-muted">{t("settings.notifOff")}</p>
          <Button onClick={onEnable} disabled={busy} className="self-start">
            {busy ? t("settings.notifEnabling") : t("settings.notifEnable")}
          </Button>
        </>
      ) : null}

      {status === "subscribed" ? (
        <>
          <p className="flex items-center gap-2 text-[0.9375rem]">
            <span
              aria-hidden="true"
              className="size-2 shrink-0 rounded-full bg-accent"
            />
            {t("settings.notifOn")}
          </p>
          <p className="text-[0.8125rem] text-muted">{t("settings.notifOnHint")}</p>
          <Button
            variant="secondary"
            onClick={onDisable}
            disabled={busy}
            className="self-start"
          >
            {busy ? t("settings.notifDisabling") : t("settings.notifDisable")}
          </Button>
        </>
      ) : null}

      {error ? <p className="text-[0.875rem] text-neg">{error}</p> : null}
    </div>
  );
}

const TONES = {
  muted: "bg-surface-alt text-ink-soft",
  warn: "bg-warn-soft text-warn",
  neg: "bg-neg-soft text-neg",
} as const;

function Note({
  tone,
  title,
  children,
}: {
  tone: keyof typeof TONES;
  title: string;
  children: ReactNode;
}) {
  return (
    // No border. Tailwind's bare `border` takes its colour from `currentColor`,
    // so this drew a `neg`- or `warn`-coloured outline round the note — the one
    // drawn line in an app whose whole rule is that separation is tonal (§4.9).
    <div className={cn("rounded-card px-3 py-2.5 text-[0.875rem]", TONES[tone])}>
      <p className="font-medium">{title}</p>
      <div className="mt-1">{children}</div>
    </div>
  );
}

/* ---------------------------------------------------------------- switch */

/**
 * A real switch: `role="switch"` with `aria-checked`, a `<button>` so it is in
 * the tab order and answers Space and Enter for free, and named by the row's own
 * text rather than by a guess. The hit area is padded out to the shared 44px
 * floor even though the track itself is smaller.
 */
function Switch({
  checked,
  onChange,
  title,
  hint,
  control,
  disabled = false,
}: {
  checked: boolean;
  onChange(next: boolean): void;
  title: string;
  hint?: string;
  /** Sits between the words and the toggle, and goes when the toggle goes. */
  control?: ReactNode;
  disabled?: boolean;
}) {
  const id = useId();

  return (
    <div className="flex items-center gap-3 py-1 pr-2 pl-4">
      <div className="min-w-0 flex-1 py-1.5">
        <p id={`${id}-label`} className="text-[0.9375rem]">
          {title}
        </p>
        {hint ? (
          <p id={`${id}-hint`} className="text-[0.8125rem] text-muted">
            {hint}
          </p>
        ) : null}
      </div>
      {control}
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        aria-labelledby={`${id}-label`}
        aria-describedby={hint ? `${id}-hint` : undefined}
        disabled={disabled}
        onClick={() => onChange(!checked)}
        className={cn(
          "grid min-h-[var(--touch)] min-w-[var(--touch)] shrink-0 place-items-center",
          "rounded-card disabled:cursor-not-allowed",
        )}
      >
        <span
          aria-hidden="true"
          className={cn(
            "flex h-6 w-11 items-center rounded-pill ring-1 transition-colors",
            checked ? "bg-accent ring-accent" : "bg-surface-alt ring-line",
            disabled && "opacity-50",
          )}
        >
          <span
            className={cn(
              "size-4 rounded-full transition-transform",
              // `bg-ground` reads light on the dark accent and dark on the mint
              // one, so the thumb stays visible in both themes without a
              // `dark:` variant.
              checked ? "translate-x-[1.5rem] bg-ground" : "translate-x-[0.25rem] bg-muted",
            )}
          />
        </span>
      </button>
    </div>
  );
}

/* ------------------------------------------------------------- lead time */

/**
 * How much notice, as one line of pills.
 *
 * One line and it scrolls, because seven of these stacked or wrapped would put
 * a third of a phone screen under a switch somebody sets once — and this screen
 * was just tightened, not loosened. The row is the same pill the wish filters
 * use: amber fill for the one that holds, a tonal hover for the rest, no
 * outlines anywhere.
 *
 * `radiogroup` rather than a set of `aria-pressed` buttons: exactly one of them
 * is true at a time, and arrow keys should walk the row.
 */
/**
 * How much notice, in the row rather than under it.
 *
 * A native `<select>`, deliberately. On a phone it opens the platform's own
 * wheel — a better way to choose one of seven than a strip to be scrolled — and
 * it arrives keyboard-navigable and correctly announced without a line of code
 * from us. The only work is undoing the browser's chrome so it reads as part of
 * this language: no border, a tonal step, and a chevron of our own, since
 * `appearance-none` takes the platform's away with everything else.
 */
function LeadSelect({
  label,
  presets,
  minutes,
  onChange,
}: {
  label: string;
  presets: readonly number[];
  minutes: number;
  onChange(minutes: number): void;
}) {
  const t = useT();

  /*
   * A value this screen did not offer is still shown, not silently corrected.
   * The presets cannot produce ninety minutes; the other phone can, and a
   * select that quietly re-picked the nearest option would change a setting
   * nobody touched — while looking like it was only rendering.
   */
  const options = presets.includes(minutes) ? presets : [...presets, minutes].sort((a, b) => a - b);

  return (
    <span className="relative shrink-0">
      <select
        aria-label={label}
        value={minutes}
        onChange={(event) => onChange(Number(event.target.value))}
        className={cn(
          "min-h-[var(--touch)] appearance-none rounded-pill bg-surface-alt",
          "py-1 pr-7 pl-3 text-[0.8125rem] text-ink-soft",
        )}
      >
        {options.map((preset) => (
          <option key={preset} value={preset}>
            {leadWords(t, preset)}
          </option>
        ))}
      </select>
      <ChevronDownIcon
        aria-hidden="true"
        className="pointer-events-none absolute top-1/2 right-2 size-4 -translate-y-1/2 text-muted"
        strokeWidth={1.9}
      />
    </span>
  );
}

/* ----------------------------------------------------------- quiet hours */

/** Where the switch starts somebody off. Overnight is what this is for. */
const DEFAULT_QUIET_FROM = "23:00";
const DEFAULT_QUIET_TO = "07:00";


function QuietHours({
  from,
  to,
  deviceTimeZone,
  onChange,
}: {
  from: string | null;
  to: string | null;
  deviceTimeZone: string;
  onChange(from: string | null, to: string | null): void;
}) {
  const t = useT();
  const id = useId();
  const [draftFrom, setDraftFrom] = useState(from ?? "");
  const [draftTo, setDraftTo] = useState(to ?? "");

  // Resync when the server's answer differs from what is on screen — a change
  // made on the other phone should land here too.
  useEffect(() => {
    setDraftFrom(from ?? "");
    setDraftTo(to ?? "");
  }, [from, to]);

  // Half a window is not a window. Holding the save until both ends exist keeps
  // us from writing a rule the server would have to guess about.
  function edit(nextFrom: string, nextTo: string) {
    setDraftFrom(nextFrom);
    setDraftTo(nextTo);
    if (nextFrom && nextTo) onChange(nextFrom, nextTo);
    else if (!nextFrom && !nextTo) onChange(null, null);
  }

  const half = Boolean(draftFrom) !== Boolean(draftTo);
  const active = Boolean(draftFrom && draftTo);
  const wraps = active && draftFrom > draftTo;

  /*
   * Off is a switch, not an empty pair of fields.
   *
   * Clearing both times has always meant "never quiet" — the server reads a
   * null bound as no window at all — but nothing on screen said so, and two
   * blank fields read as unfinished rather than as off. Somebody wanting to be
   * reachable at night had no way to say it and no way to see that they had.
   */
  function toggle(on: boolean) {
    if (on) {
      // Somewhere to start rather than two empty boxes. Overnight is what
      // quiet hours are for, and both ends are still there to be changed.
      const nextFrom = draftFrom || DEFAULT_QUIET_FROM;
      const nextTo = draftTo || DEFAULT_QUIET_TO;
      setDraftFrom(nextFrom);
      setDraftTo(nextTo);
      onChange(nextFrom, nextTo);
    } else {
      // The times are kept on screen, greyed, so turning it back on does not
      // ask somebody to remember what they had.
      onChange(null, null);
    }
  }

  const on = Boolean(from && to);

  return (
    <section className="flex flex-col gap-3 border-t border-line-soft px-4 py-3">
      {/* The switch *is* the heading — it already renders a title and a hint,
          and a separate <h3> above it would say the same words twice. */}
      <div className="-mx-4">
        <Switch
          checked={on}
          onChange={toggle}
          title={t("settings.quietTitle")}
          hint={on ? t("settings.quietHint") : t("settings.quietOff")}
        />
      </div>

      <div className={cn("flex flex-wrap items-end gap-3", !on && "opacity-45")}>
        <div className="flex flex-col gap-1.5">
          <label
            htmlFor={`${id}-from`}
            className="text-[0.8125rem] font-medium text-ink-soft"
          >
            {t("settings.quietFrom")}
          </label>
          <input
            id={`${id}-from`}
            type="time"
            value={draftFrom}
            onChange={(e) => edit(e.target.value, draftTo)}
            className="tabular min-h-[var(--touch)] rounded-row bg-surface-alt px-4"
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <label
            htmlFor={`${id}-to`}
            className="text-[0.8125rem] font-medium text-ink-soft"
          >
            {t("settings.quietTo")}
          </label>
          <input
            id={`${id}-to`}
            type="time"
            value={draftTo}
            onChange={(e) => edit(draftFrom, e.target.value)}
            className="tabular min-h-[var(--touch)] rounded-row bg-surface-alt px-4"
          />
        </div>
        {draftFrom || draftTo ? (
          <Button
            variant="ghost"
            onClick={() => edit("", "")}
            className="px-3 text-[0.875rem]"
          >
            {t("settings.quietReset")}
          </Button>
        ) : null}
      </div>

      {half ? (
        <p className="text-[0.8125rem] text-warn">{t("settings.quietHalf")}</p>
      ) : wraps ? (
        <p className="text-[0.8125rem] text-muted">
          {t("settings.quietWraps", { from: draftFrom, to: draftTo })}
        </p>
      ) : null}

      {/* Stated, not offered. The zone follows the device — "не беспокоить с
          23:00" means 23:00 where the person is now — and the one question a
          button could have asked here has the same answer every time. */}
      <p className="border-t border-line-soft pt-3 text-[0.8125rem] text-muted">
        {t("settings.quietTimeZone")} <span className="font-mono">{deviceTimeZone}</span>
      </p>
    </section>
  );
}

/* ------------------------------------------------------------- save badge */

function SaveBadge({ state }: { state: SaveState }) {
  const t = useT();

  if (state.kind === "saving")
    return <span aria-live="polite">{t("settings.saving")}</span>;
  if (state.kind === "saved")
    return (
      <span aria-live="polite" className="text-accent">
        {t("settings.saved")}
      </span>
    );
  if (state.kind === "error")
    return (
      <span aria-live="polite" className="text-neg">
        {t("settings.notSaved")}
      </span>
    );
  return null;
}
